import * as React from 'react';
import type {
    ClusterDayAssignment,
    ClusterKind,
    RateSnapshotData,
    RateSnapshotListItem,
} from './types';
import { ClusterTotalPriceCell } from './ClusterTotalPriceCell';

export interface RoomIsland {
    id: string;
    chain: RoomIslandEntry[];
    dateRange: string;
    displayName: string;
    startDate: string;
    endDate: string;
}

export interface RoomIslandEntry {
    reservationRoomId: string;
    roomType: string | null;
    displayName: string;
    checkIn: string;
    checkOut: string;
}

export interface ProgramIsland {
    id: string;
    chain: ProgramIslandEntry[];
    dateRange: string;
    displayName: string;
    startDate: string;
    endDate: string;
}

export interface ProgramIslandEntry {
    reservationProgramId: string;
    programType: string;
    displayName: string;
    start: string;
    end: string;
}

interface ClusterListProps {
    roomIslands: RoomIsland[];
    programIslands: ProgramIsland[];
    clusterAssignments: ClusterDayAssignment[];
    editingClusterId: string | null;
    onEditCluster: (clusterId: string | null) => void;
    snapshot: RateSnapshotListItem | undefined;
    targetCurrent: RateSnapshotData | null;
    formatCurrency: (amount: number) => string;
}

export function ClusterAssignmentPanel({
    roomIslands, programIslands,
    clusterAssignments, editingClusterId, onEditCluster,
    snapshot, targetCurrent, formatCurrency,
}: ClusterListProps) {
    return (
        <div className="flex flex-col gap-3">
            {roomIslands.length > 0 && (
                <ClusterGroup
                    title="Rooms"
                    kind="ROOM"
                    rows={roomIslands.map(i => ({
                        id: i.id,
                        displayName: i.displayName,
                        dateRange: i.dateRange,
                        startDate: i.startDate,
                        endDate: i.endDate,
                        segmentCount: i.chain.length,
                        memberIds: i.chain.map(c => c.reservationRoomId),
                    }))}
                    clusterAssignments={clusterAssignments}
                    editingClusterId={editingClusterId}
                    onEditCluster={onEditCluster}
                    snapshot={snapshot}
                    targetCurrent={targetCurrent}
                    formatCurrency={formatCurrency}
                />
            )}
            {programIslands.length > 0 && (
                <ClusterGroup
                    title="Programs"
                    kind="PROGRAM"
                    rows={programIslands.map(i => ({
                        id: i.id,
                        displayName: i.displayName,
                        dateRange: i.dateRange,
                        startDate: i.startDate,
                        endDate: i.endDate,
                        segmentCount: i.chain.length,
                        memberIds: i.chain.map(c => c.reservationProgramId),
                    }))}
                    clusterAssignments={clusterAssignments}
                    editingClusterId={editingClusterId}
                    onEditCluster={onEditCluster}
                    snapshot={snapshot}
                    targetCurrent={targetCurrent}
                    formatCurrency={formatCurrency}
                />
            )}
        </div>
    );
}

interface ClusterRow {
    id: string;
    displayName: string;
    dateRange: string;
    startDate: string;
    endDate: string;
    segmentCount: number;
    memberIds: string[];
}

interface ClusterGroupProps {
    title: string;
    kind: ClusterKind;
    rows: ClusterRow[];
    clusterAssignments: ClusterDayAssignment[];
    editingClusterId: string | null;
    onEditCluster: (clusterId: string | null) => void;
    snapshot: RateSnapshotListItem | undefined;
    targetCurrent: RateSnapshotData | null;
    formatCurrency: (amount: number) => string;
}

function ClusterGroup({
    title, kind, rows, clusterAssignments, editingClusterId, onEditCluster,
    snapshot, targetCurrent, formatCurrency,
}: ClusterGroupProps) {
    return (
        <div>
            <h6 className="font-semibold mb-2">{title}</h6>
            {rows.map(row => {
                const assignment = clusterAssignments.find(a => a.clusterId === row.id);
                const dayCount = assignment?.days.length ?? 0;
                const isEditing = editingClusterId === row.id;

                return (
                    <div
                        key={row.id}
                        className={`border rounded p-3 mb-2 ${isEditing ? 'border-primary' : ''}`}
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                                <div>
                                    <span className="font-medium text-sm">{row.displayName}</span>
                                    {row.segmentCount > 1 && (
                                        <span className="badge bg-info text-xs ms-2">{row.segmentCount} segments</span>
                                    )}
                                </div>
                                <div className="text-xs text-gray-500">{row.dateRange}</div>
                                <div className="text-xs text-gray-600 mt-1 mb-2">
                                    {dayCount === 0
                                        ? 'No dates set'
                                        : dayCount === 1 ? '1 date set' : `${dayCount} dates set`}
                                </div>
                                <ClusterTotalPriceCell
                                    cluster={{
                                        id: row.id,
                                        kind,
                                        memberIds: row.memberIds,
                                        startDate: row.startDate,
                                        endDate: row.endDate,
                                        displayName: row.displayName,
                                    }}
                                    snapshot={snapshot}
                                    targetCurrent={targetCurrent}
                                    assignment={assignment}
                                />
                            </div>
                            <button
                                type="button"
                                className={`btn btn-sm ${isEditing ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => onEditCluster(isEditing ? null : row.id)}
                            >
                                {isEditing ? 'Editing' : 'Edit'}
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
